import { useEffect, useMemo, useState } from "react";
import * as superadminService from "../services/superadminService";
import { useToast } from "../components/ui/use-toast";
import { Pagination } from "../components/table/DataTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const moneyFmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const SuperadminClientInvestmentsPage = () => {
  const [rows, setRows] = useState<superadminService.SuperadminClientInvestmentRow[]>(
    []
  );
  const [summary, setSummary] = useState<superadminService.SuperadminBoardSummary>({
    projectInvested: 0,
    packInvested: 0,
    totalInvested: 0,
  });
  const [period, setPeriod] =
    useState<superadminService.SuperadminBoardPeriod>("all");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<superadminService.SuperadminBoardMeta>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    period: "all",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      try {
        const response = await superadminService.listClientInvestmentsBoard({
          period,
          page,
          limit: 15,
        });
        setRows(response.data.rows);
        setSummary(response.data.summary);
        if (response.meta) {
          setMeta(response.meta);
        }
      } catch (error) {
        toast({
          title: "Chargement impossible",
          description:
            (error as string) ??
            "Impossible de charger le tableau des investissements clients.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, [toast, period, page]);

  const periodLabel = useMemo(() => {
    if (period === "day") return "Dernières 24h";
    if (period === "week") return "7 derniers jours";
    if (period === "month") return "30 derniers jours";
    return "Depuis le début";
  }, [period]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold text-foreground dark:text-white">
          Board Superadmin
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          Vue des montants investis par client ({periodLabel}).
        </p>
      </div>

      <div className="flex max-w-xs flex-col gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground dark:text-white/60">
          Filtrer par période
        </span>
        <Select
          value={period}
          onValueChange={(value) => {
            setPeriod(value as superadminService.SuperadminBoardPeriod);
            setPage(1);
          }}>
          <SelectTrigger className="h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Jour (24h)</SelectItem>
            <SelectItem value="week">Semaine (7 jours)</SelectItem>
            <SelectItem value="month">Mois (30 jours)</SelectItem>
            <SelectItem value="all">Tout</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-border bg-muted/30 p-4 dark:border-white/10 dark:bg-white/5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground dark:text-white/60">
            Investi projets
          </p>
          <p className="mt-1 text-xl font-semibold text-foreground dark:text-white">
            {moneyFmt.format(summary.projectInvested)}
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-muted/30 p-4 dark:border-white/10 dark:bg-white/5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground dark:text-white/60">
            Investi packs
          </p>
          <p className="mt-1 text-xl font-semibold text-foreground dark:text-white">
            {moneyFmt.format(summary.packInvested)}
          </p>
        </div>
        <div className="rounded-2xl border border-cyan-500/40 bg-cyan-500/10 p-4 dark:border-cyan-400/40 dark:bg-cyan-500/15">
          <p className="text-xs uppercase tracking-wide text-cyan-900/80 dark:text-cyan-200/70">
            Total investi
          </p>
          <p className="mt-1 text-xl font-semibold text-cyan-950 dark:text-cyan-100">
            {moneyFmt.format(summary.totalInvested)}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border dark:border-white/10">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50 dark:bg-white/5">
            <tr className="text-left text-muted-foreground dark:text-white/70">
              <th className="px-4 py-3 font-medium">Client</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 text-right font-medium">Projets</th>
              <th className="px-4 py-3 text-right font-medium">Packs</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.userId}
                className="border-t border-border/70 dark:border-white/10">
                <td className="px-4 py-3 font-medium text-foreground dark:text-white">
                  {row.firstName} {row.lastName}
                </td>
                <td className="px-4 py-3 text-muted-foreground dark:text-white/70">
                  {row.email}
                </td>
                <td className="px-4 py-3 text-right text-foreground dark:text-white/90">
                  {moneyFmt.format(row.projectInvested)}
                </td>
                <td className="px-4 py-3 text-right text-foreground dark:text-white/90">
                  {moneyFmt.format(row.packInvested)}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-cyan-700 dark:text-cyan-300">
                  {moneyFmt.format(row.totalInvested)}
                </td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-muted-foreground dark:text-white/60">
                  Aucun investissement client trouvé.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {meta.totalPages > 1 && (
        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          totalItems={meta.total}
          itemsPerPage={meta.limit}
          onPageChange={setPage}
          isLoading={isLoading}
          ariaLabelPrevious="Page précédente"
          ariaLabelNext="Page suivante"
        />
      )}
    </div>
  );
};

export default SuperadminClientInvestmentsPage;
